import React, { createContext, useState, useContext, useEffect } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { auth, db } from '../private/firebaseConfig';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

export const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastAuthData, setLastAuthData] = useState(null);

  // Fonction pour charger les données utilisateur depuis AsyncStorage au démarrage
  const loadStoredUser = async () => {
    try {
      const storedUser = await AsyncStorage.getItem('user');
      const storedUserType = await AsyncStorage.getItem('userType');
      const storedLastAuth = await AsyncStorage.getItem('lastAuthData');
      
      if (storedLastAuth) {
        setLastAuthData(JSON.parse(storedLastAuth));
      }
      
      if (storedUser) {
        setUser(JSON.parse(storedUser));
        setUserType(storedUserType);
      }
    } catch (error) {
      console.error('Error loading stored user:', error);
    }
  };

  useEffect(() => {
    loadStoredUser();

    // Nettoyage unique: supprimer tout token anonyme corrompu
    const cleanupCorruptedAuth = async () => {
      try {
        const currentUser = auth.currentUser;
        if (currentUser && currentUser.isAnonymous) {
          console.log('Nettoyage: suppression du token anonyme corrompu');
          await signOut(auth);
        }
      } catch (e) {
        console.log('Cleanup error (ignoré):', e.message);
      }
    };
    cleanupCorruptedAuth();

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Définir un timeout pour limiter le temps d'attente de la vérification
      let timeoutId = setTimeout(() => {
        console.log("Vérification auth timeout, utilisant les données stockées");
        // Si le timeout se déclenche, on garde les données déjà chargées
        // et on arrête le chargement
        setLoading(false);
      }, 6000); // 6 secondes max pour la vérification

      if (firebaseUser) {
        try {
          // Vérifier dans la collection admin d'abord
          const adminDoc = await getDoc(doc(db, 'admin', firebaseUser.uid));
          
          if (adminDoc.exists()) {
            const adminData = {
              ...firebaseUser,
              ...adminDoc.data(),
              id: firebaseUser.uid
            };
            setUser(adminData);
            setUserType('admin');
            
            // Sauvegarder dans AsyncStorage
            await AsyncStorage.setItem('user', JSON.stringify(adminData));
            await AsyncStorage.setItem('userType', 'admin');
            
            // Sauvegarder les dernières données d'authentification valides
            const lastAuthData = { user: adminData, userType: 'admin', timestamp: Date.now() };
            await AsyncStorage.setItem('lastAuthData', JSON.stringify(lastAuthData));
            setLastAuthData(lastAuthData);
            
          } else {
            // Vérifier dans la collection users
            const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
            
            if (userDoc.exists()) {
              const userData = {
                ...firebaseUser,
                ...userDoc.data(),
                id: firebaseUser.uid
              };
              setUser(userData);
              setUserType('user');
              
              await AsyncStorage.setItem('user', JSON.stringify(userData));
              await AsyncStorage.setItem('userType', 'user');
              
              // Sauvegarder les dernières données d'authentification valides
              const lastAuthData = { user: userData, userType: 'user', timestamp: Date.now() };
              await AsyncStorage.setItem('lastAuthData', JSON.stringify(lastAuthData));
              setLastAuthData(lastAuthData);
              
            } else {
              // Vérifier dans la collection centers
              const centerDoc = await getDoc(doc(db, 'centers', firebaseUser.uid));
              if (centerDoc.exists()) {
                const centerData = {
                  ...firebaseUser,
                  ...centerDoc.data(),
                  id: firebaseUser.uid
                };
                setUser(centerData);
                setUserType('center');
                
                await AsyncStorage.setItem('user', JSON.stringify(centerData));
                await AsyncStorage.setItem('userType', 'center');
                
                // Sauvegarder les dernières données d'authentification valides
                const lastAuthData = { user: centerData, userType: 'center', timestamp: Date.now() };
                await AsyncStorage.setItem('lastAuthData', JSON.stringify(lastAuthData));
                setLastAuthData(lastAuthData);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          
          // En cas d'erreur de réseau, vérifier s'il y a des données récentes disponibles
          if (lastAuthData && (Date.now() - lastAuthData.timestamp < 7 * 24 * 60 * 60 * 1000)) { // 7 jours
            console.log('Utilisation des données sauvegardées suite à une erreur');
            setUser(lastAuthData.user);
            setUserType(lastAuthData.userType);
          } else {
            // Si pas de données récentes ou données trop anciennes, nettoyer
            setUser(null);
            setUserType(null);
            await AsyncStorage.multiRemove(['user', 'userType']);
          }
        }
      } else {
        // Utilisateur déconnecté
        setUser(null);
        setUserType(null);
        await AsyncStorage.multiRemove(['user', 'userType', 'lastAuthData']);
      }
      
      // Annuler le timeout et terminer le chargement
      clearTimeout(timeoutId);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        userType,
        loading,
        isAuthenticated: !!user,
        lastAuthData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};